/**
 * The coarse world map that floats inside the page background.
 *
 * A direct port of `WorldMap` in Android's `ui/components/AppBackground.kt`, and
 * the rows below are copied from it character for character. It is a hand-typed
 * land mask rather than an image for the same reason it is one on Android: at
 * this resolution the string is smaller than any vector asset, and a bitmap
 * would have to ship at several densities to stay crisp. The cost of that choice
 * is that a single mistyped or short row silently shifts every continent after
 * it — invisible at 5% opacity behind a dashboard, obvious in a screenshot — so
 * `src/lib/worldMap.test.ts` guards the grid's shape exactly as Android's
 * `WorldMapTest` does.
 *
 * Lives under `components/ui/` rather than `lib/` because it is pure decoration
 * with no bearing on what gets measured or submitted; its test lives in `lib/`
 * with the rest of the node-environment suite.
 */

/** Land cell. Anything else in a row is sea. */
const LAND = '#';

/** 28 rows of 60 columns, equirectangular, one character per cell. */
export const WORLD_MAP_ROWS: string[] = [
  '.....................#####..................................',
  '.....................#####..................................',
  '..##################.#####.....####.....####################',
  '..##################.#####.....####.....####################',
  '.......#############........################################',
  '.......#############........################################',
  '.........#########..........################################',
  '.........#########..........################################',
  '.........#########..........#######.....##############......',
  '.........#########.........#########....##############......',
  '.........#########.........#########....##############......',
  '...........####............#########.....####.####..........',
  '...........####............#########.....####.####..........',
  '...............##..........#########.....####.####..........',
  '...............#####.......###########...####.####..........',
  '.................###........##########........#######.......',
  '.................###........##########........#######.......',
  '.................#######....##########........#######.......',
  '.................#######........#####.........#######.......',
  '.................#######........#####............#######....',
  '.................#######........#####............#######....',
  '..................#####.........#####............#######....',
  '..................#####.........#####............#######....',
  '..................#####.........#####............#######....',
  '..................#####...................................##',
  '.................##.......................................##',
  '.................##.........................................',
  '.................##.........................................',
];

/** Columns per row. The column index is what carries longitude. */
export const WORLD_MAP_COLUMNS = 60;

/**
 * Cached because the answer never changes and the background repaints on every
 * resize and every theme change. Android resolves the same list once as a `val`
 * on an object; a function is the closest equivalent that keeps the module free
 * of import-time work.
 *
 * The array is shared with every caller — read it, never mutate it.
 */
let dots: { x: number; y: number }[] | null = null;

/**
 * Land cells as centres in a 0..1 square, ready to be scaled to any canvas size.
 *
 * Normalised rather than in pixels so the same map can be painted at any size
 * without re-deriving it, which is also why an out-of-range value would be drawn
 * off-screen instead of reported — hence the unit-square assertion in the test.
 */
export function worldMapDots(): { x: number; y: number }[] {
  if (dots) return dots;
  const rows = WORLD_MAP_ROWS.length;
  const out: { x: number; y: number }[] = [];
  WORLD_MAP_ROWS.forEach((line, row) => {
    for (let column = 0; column < line.length; column += 1) {
      if (line[column] !== LAND) continue;
      // +0.5 puts the dot at the centre of its cell, not its top-left corner;
      // without it the whole map drifts half a cell up and to the left.
      out.push({ x: (column + 0.5) / WORLD_MAP_COLUMNS, y: (row + 0.5) / rows });
    }
  });
  dots = out;
  return dots;
}
